import { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, Link } from "wouter";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { InsertClient, Client } from "@shared/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import AirDatepicker from "air-datepicker";
import "air-datepicker/air-datepicker.css";
type AirDatepickerInstance = InstanceType<typeof AirDatepicker>;

import { Button } from "@/components/ui/button";
import { ChevronLeft, CalendarIcon, Instagram, Facebook, Twitter } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { format, parse } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Loader2 } from "lucide-react";

// Create a schema for contact creation with explicit string-based date handling
const contactFormSchema = z.object({
  // Personal information
  firstName: z.string().min(1, "First name is required"),
  middleName: z.string().optional().nullable(),
  lastName: z.string().min(1, "Last name is required"),
  suffix: z.string().optional().nullable(),
  preferredName: z.string().optional().nullable(),
  salutation: z.string().optional().nullable(),
  gender: z.string().optional().nullable(),
  
  // Contact information
  email: z.string().email().optional().nullable(),
  secondaryEmail: z.string().email().optional().nullable(),
  phone: z.string().optional().nullable(),
  workPhone: z.string().optional().nullable(),
  mobile: z.string().optional().nullable(),
  
  // Mailing address
  address: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  state: z.string().optional().nullable(),
  zipCode: z.string().optional().nullable(),
  country: z.string().optional().nullable(),
  
  // Billing address
  billingAddress: z.string().optional().nullable(),
  billingCity: z.string().optional().nullable(),
  billingState: z.string().optional().nullable(),
  billingZipCode: z.string().optional().nullable(),
  billingCountry: z.string().optional().nullable(),
  sameAsMailing: z.boolean().optional().default(false),
  
  // Social media
  instagram: z.string().optional().nullable(),
  facebook: z.string().optional().nullable(),
  twitter: z.string().optional().nullable(),
  
  // Emergency contact
  emergencyContactName: z.string().optional().nullable(),
  emergencyContactRelationship: z.string().optional().nullable(),
  emergencyContactPhone: z.string().optional().nullable(),
  emergencyClientEmail: z.string().email().optional().nullable(),
  
  // Health information
  hasMovementRestrictions: z.boolean().optional().default(false),
  movementRestrictionsNotes: z.string().optional().nullable(),
  
  // We handle the date as a Date in the UI but will convert to string for API submission
  birthdate: z.union([z.date(), z.string().transform(val => new Date(val))]).optional().nullable(),
  notes: z.string().optional().nullable(),
  avatarUrl: z.string().optional().nullable(),
});

const toValidDate = (value: Date | string | null | undefined): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

export function ClientForm() {
  const [location, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Determine if we're in edit mode by checking the URL
  const isEditMode = location.includes('/edit');
  const contactId = useMemo(() => {
    if (!isEditMode) {
      return null;
    }

    const parts = location.split('/');
    const candidate = parts[2];
    if (!candidate) {
      return null;
    }

    const parsed = Number.parseInt(candidate, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }, [isEditMode, location]);
  
  // Fetch existing contact data when in edit mode
  const { data: existingContact, isLoading: isLoadingContact } = useQuery<Client>({
    queryKey: [`/api/clients/${contactId}`],
    enabled: contactId != null && isEditMode,
  });

  // Initialize the form with default values
  const form = useForm<z.infer<typeof contactFormSchema>>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      // Personal information
      firstName: "",
      middleName: null,
      lastName: "",
      suffix: null,
      preferredName: null,
      salutation: null,
      gender: null,
      
      // Contact information
      email: null,
      secondaryEmail: null,
      phone: null,
      workPhone: null,
      mobile: null,
      
      // Mailing address
      address: null,
      city: null,
      state: null,
      zipCode: null,
      country: null,
      
      // Billing address
      billingAddress: null,
      billingCity: null,
      billingState: null,
      billingZipCode: null,
      billingCountry: null,
      sameAsMailing: false,
      
      // Social media
      instagram: null,
      facebook: null,
      twitter: null,
      
      // Emergency contact
      emergencyContactName: null,
      emergencyContactRelationship: null,
      emergencyContactPhone: null,
      emergencyClientEmail: null,
      
      // Health information
      hasMovementRestrictions: false,
      movementRestrictionsNotes: null,
      
      birthdate: null,
      notes: null,
      avatarUrl: null,
    },
  });
  
  // When in edit mode and we have the existing contact data, populate the form
  useEffect(() => {
    if (isEditMode && existingContact) {
      // Convert birthdate string to Date object if it exists
      // This handles both string and Date types for maximum compatibility
      const birthdate = toValidDate(existingContact.birthdate);
      const contactRecord = existingContact as Record<string, unknown>;
      const sameAsMailing = typeof contactRecord.sameAsMailing === 'boolean'
        ? (contactRecord.sameAsMailing as boolean)
        : false;
      
      // Reset the form with existing data
      form.reset({
        ...existingContact,
        birthdate,
        // Ensure boolean fields are properly set
        sameAsMailing,
        hasMovementRestrictions: existingContact.hasMovementRestrictions ?? false,
      });
    }
  }, [existingContact, isEditMode, form]);

  // Create mutation for saving new contact
  const createContactMutation = useMutation({
    mutationFn: async (values: z.infer<typeof contactFormSchema>) => {
      const birthdateValue = toValidDate(values.birthdate);

      const payload = {
        ...values,
        birthdate: birthdateValue ? format(birthdateValue, 'yyyy-MM-dd') : null,
      };

      return apiRequest("POST", "/api/clients", payload);
    },
    onSuccess: (data) => {
      toast({
        title: "Contact created",
        description: "The contact has been created successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      setLocation(`/clients-old/${data.id}`);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create contact. Please try again.",
        variant: "destructive",
      });
      console.error("Error creating contact:", error);
    },
  });
  
  // Update mutation for modifying existing contact
  const updateContactMutation = useMutation({
    mutationFn: async (values: z.infer<typeof contactFormSchema> & { id: number }) => {
      const birthdateValue = toValidDate(values.birthdate);

      const payload = {
        ...values,
        birthdate: birthdateValue ? format(birthdateValue, 'yyyy-MM-dd') : null,
      };

      return apiRequest("PATCH", `/api/clients/${values.id}`, payload);
    },
    onSuccess: (data) => {
      toast({
        title: "Contact updated",
        description: "The contact has been updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: [`/api/clients/${data.id}`] });
      setLocation(`/clients-old/${data.id}`);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update contact. Please try again.",
        variant: "destructive",
      });
      console.error("Error updating contact:", error);
    },
  });

  const onSubmit = (values: z.infer<typeof contactFormSchema>) => {
    if (isEditMode && contactId) {
      updateContactMutation.mutate({ ...values, id: contactId });
    } else {
      createContactMutation.mutate(values);
    }
  };

  // Show loading state when fetching contact data in edit mode
  if (isEditMode && isLoadingContact) {
    return (
      <div className="py-8 max-w-[1200px] mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center">
            <Button variant="outline" size="sm" asChild className="mr-4">
              <Link to="/clients-old">
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back to Clients
              </Link>
            </Button>
            <h1 className="text-2xl font-bold">Loading Contact...</h1>
          </div>
        </div>
        <div className="flex items-center justify-center p-12">
          <div className="flex flex-col items-center gap-2">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
            <p className="text-sm text-muted-foreground">Loading contact information...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="py-8 max-w-[1200px] mx-auto">
      {/* Header with back button */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center">
          <Button variant="outline" size="sm" asChild className="mr-4">
            <Link to="/clients-old">
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Clients
            </Link>
          </Button>
          <h1 className="text-2xl font-bold">
            {isEditMode ? 'Edit Contact' : 'Create New Contact'}
          </h1>
        </div>
      </div>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Personal Information</CardTitle>
          <CardDescription>
            Add the basic information for this contact.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Personal Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter first name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="middleName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Middle Name</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Enter middle name" 
                            {...field} 
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value || null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter last name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="suffix"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Suffix</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Jr., Sr., III, etc." 
                            {...field} 
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value || null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="preferredName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Preferred Name</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Nickname or preferred name" 
                            {...field} 
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value || null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="salutation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Salutation</FormLabel>
                        <Select
                          value={field.value || ""}
                          onValueChange={(value) => field.onChange(value || null)}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select salutation" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Mr.">Mr.</SelectItem>
                            <SelectItem value="Mrs.">Mrs.</SelectItem>
                            <SelectItem value="Ms.">Ms.</SelectItem>
                            <SelectItem value="Dr.">Dr.</SelectItem>
                            <SelectItem value="Prof.">Prof.</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="gender"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Gender</FormLabel>
                        <Select
                          value={field.value || ""}
                          onValueChange={(value) => field.onChange(value || null)}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select gender" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Male">Male</SelectItem>
                            <SelectItem value="Female">Female</SelectItem>
                            <SelectItem value="Non-binary">Non-binary</SelectItem>
                            <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="birthdate"
                    render={({ field }) => {
                      const datePickerRef = useRef<HTMLDivElement | null>(null);
                      const datepickerInstance = useRef<AirDatepickerInstance | null>(null);

                      const resolveDateValue = (value: typeof field.value) => toValidDate(value);

                      const [displayValue, setDisplayValue] = useState<string>(() => {
                        const initialDate = resolveDateValue(field.value);
                        return initialDate ? format(initialDate, "MM/dd/yyyy") : "";
                      });
                      
                      // Update display value when field value changes
                      useEffect(() => {
                        const nextDate = resolveDateValue(field.value);
                        setDisplayValue(nextDate ? format(nextDate, "MM/dd/yyyy") : "");
                      }, [field.value]);
                      
                      // Initialize datepicker after component mounts
                      useEffect(() => {
                        const container = datePickerRef.current;
                        if (!container || datepickerInstance.current) return;

                        const inputEl = document.createElement('input');
                        inputEl.type = 'text';
                        inputEl.placeholder = 'MM/DD/YYYY';
                        inputEl.readOnly = true;
                        const initialDate = resolveDateValue(field.value);
                        inputEl.value = initialDate ? format(initialDate, 'MM/dd/yyyy') : '';
                        inputEl.className = 'w-full h-10 px-3 rounded-md border border-input bg-background text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

                        container.innerHTML = '';
                        container.appendChild(inputEl);

                        datepickerInstance.current = new AirDatepicker(inputEl, {
                          locale: {
                            days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
                            daysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
                            daysMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
                            months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
                            monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                            today: 'Today',
                            clear: 'Clear',
                            dateFormat: 'MM/dd/yyyy',
                            timeFormat: 'hh:mm aa',
                            firstDay: 0,
                          },
                          dateFormat: 'MM/dd/yyyy',
                          position: 'bottom left',
                          autoClose: true,
                          minDate: new Date(1920, 0, 1),
                          maxDate: new Date(),
                          view: 'days',
                          onSelect: ({ date }) => {
                            const selected = Array.isArray(date) ? date[0] : date;
                            if (selected instanceof Date && !Number.isNaN(selected.getTime())) {
                              field.onChange(selected);
                              setDisplayValue(format(selected, 'MM/dd/yyyy'));
                            } else {
                              field.onChange(null);
                              setDisplayValue('');
                            }
                          },
                          selectedDates: initialDate ? [initialDate] : [],
                          buttons: ['today', 'clear'],
                        });

                        const iconContainer = document.createElement('div');
                        iconContainer.className = 'absolute right-3 top-1/2 -translate-y-1/2 opacity-50';
                        iconContainer.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-calendar"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>';

                        const containerDiv = container.parentElement;
                        if (containerDiv) {
                          containerDiv.style.position = 'relative';
                          containerDiv.appendChild(iconContainer);
                        }

                        const handleIconClick = () => {
                          datepickerInstance.current?.show();
                        };
                        iconContainer.addEventListener('click', handleIconClick);

                        if (initialDate && datepickerInstance.current) {
                          datepickerInstance.current.selectDate(initialDate);
                        }

                        return () => {
                          iconContainer.removeEventListener('click', handleIconClick);
                          if (datepickerInstance.current) {
                            datepickerInstance.current.destroy();
                            datepickerInstance.current = null;
                          }
                        };
                      }, [field.value]);

                      useEffect(() => {
                        const nextDate = resolveDateValue(field.value);
                        if (!datepickerInstance.current) return;

                        if (nextDate) {
                          datepickerInstance.current.selectDate(nextDate, { silent: true });
                        } else {
                          datepickerInstance.current.clear();
                        }
                      }, [field.value]);
                      return (
                        <FormItem className="flex flex-col">
                          <FormLabel>Birthdate</FormLabel>
                          <FormControl>
                            <div ref={datePickerRef} className="air-datepicker-container"></div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4 pt-4">
                <h3 className="text-lg font-medium">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Primary Email</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="Enter email address"
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value || null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="secondaryEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Secondary Email</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="Enter secondary email address"
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value || null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Home Phone</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Enter home phone number" 
                            {...field} 
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value || null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="workPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Work Phone</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Enter work phone number" 
                            {...field} 
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value || null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="mobile"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mobile Phone</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Enter mobile number" 
                            {...field} 
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value || null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Mailing Address */}
              <div className="space-y-4 pt-4">
                <h3 className="text-lg font-medium">Mailing Address</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Street Address</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Enter street address" 
                            {...field} 
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value || null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Enter city" 
                            {...field} 
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value || null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>State / Province</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Enter state or province" 
                            {...field} 
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value || null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="zipCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Zip / Postal Code</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Enter zip or postal code" 
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value || null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Country</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Enter country" 
                            {...field} 
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value || null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Billing Address */}
              <div className="space-y-4 pt-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">Billing Address</h3>
                  <FormField
                    control={form.control}
                    name="sameAsMailing"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                        <FormControl>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="sameAsMailing"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                            <label
                              htmlFor="sameAsMailing"
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              Same as mailing address
                            </label>
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
                {!form.watch("sameAsMailing") && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="billingAddress"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Street Address</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Enter billing street address" 
                              {...field} 
                              value={field.value || ""}
                              onChange={(e) => {
                                field.onChange(e.target.value || null);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="billingCity"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>City</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Enter billing city" 
                              {...field} 
                              value={field.value || ""}
                              onChange={(e) => {
                                field.onChange(e.target.value || null);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="billingState"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>State / Province</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Enter billing state or province" 
                              {...field} 
                              value={field.value || ""}
                              onChange={(e) => {
                                field.onChange(e.target.value || null);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="billingZipCode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Zip / Postal Code</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Enter billing zip or postal code" 
                              {...field}
                              value={field.value || ""}
                              onChange={(e) => {
                                field.onChange(e.target.value || null);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="billingCountry"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Country</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Enter billing country" 
                              {...field} 
                              value={field.value || ""}
                              onChange={(e) => {
                                field.onChange(e.target.value || null);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </div>

              {/* Social Media */}
              <div className="space-y-4 pt-4">
                <h3 className="text-lg font-medium">Social Media</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <FormField
                    control={form.control}
                    name="instagram"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center">
                          <Instagram className="h-4 w-4 mr-2" />
                          Instagram
                        </FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="@username" 
                            {...field} 
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value || null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="facebook"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center">
                          <Facebook className="h-4 w-4 mr-2" />
                          Facebook
                        </FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Username or profile URL" 
                            {...field} 
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value || null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="twitter"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center">
                          <Twitter className="h-4 w-4 mr-2" />
                          X / Twitter
                        </FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="@username" 
                            {...field} 
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value || null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="space-y-4 pt-4">
                <h3 className="text-lg font-medium">Emergency Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="emergencyContactName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Full name of emergency contact" 
                            {...field} 
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value || null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="emergencyContactRelationship"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Relationship</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Spouse, Parent, Friend, etc." 
                            {...field} 
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value || null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="emergencyContactPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Emergency contact phone number" 
                            {...field} 
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value || null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="emergencyClientEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input 
                            type="email"
                            placeholder="Emergency contact email" 
                            {...field} 
                            value={field.value || ""}
                            onChange={(e) => {
                              field.onChange(e.target.value || null);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Health Information */}
              <div className="space-y-4 pt-4">
                <h3 className="text-lg font-medium">Health Information</h3>
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="hasMovementRestrictions"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 pb-2">
                        <FormControl>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="hasMovementRestrictions"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                            <label
                              htmlFor="hasMovementRestrictions"
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              Has movement restrictions or accessibility needs
                            </label>
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {form.watch("hasMovementRestrictions") && (
                    <FormField
                      control={form.control}
                      name="movementRestrictionsNotes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Details about movement restrictions</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Provide details about mobility needs, assistive devices, or accessibility requirements"
                              className="min-h-[100px]"
                              {...field}
                              value={field.value || ""}
                              onChange={(e) => {
                                field.onChange(e.target.value || null);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-4 pt-4">
                <h3 className="text-lg font-medium">Additional Notes</h3>
                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Notes</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Add any additional notes about this contact"
                          className="min-h-[100px]"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => {
                            field.onChange(e.target.value || null);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setLocation("/clients-old")}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  disabled={createContactMutation.isPending || updateContactMutation.isPending}
                >
                  {createContactMutation.isPending || updateContactMutation.isPending ? (
                    <>
                      <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-r-transparent"></span>
                      {isEditMode ? "Updating..." : "Creating..."}
                    </>
                  ) : (
                    isEditMode ? "Update Contact" : "Create Contact"
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
